import { PaymentMethod, TokenCredentials } from '@lucidtech/las-sdk-browser';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { PENDING_PLAN_ID_KEY } from '@/constants';
import { Plans } from '@/features/settings';
import { toOrganizationId, toPaymentMethodId } from '@/hooks/api';
import { getPendingPaymentMethodKey } from '@/utils';

const PlansSearch = z
  .object({
    redirect_status: fallback(z.optional(z.string()), undefined),
    setup_intent_client_secret: fallback(z.optional(z.string()), undefined),
    upgradePlanId: fallback(z.optional(z.string()), undefined),
  })
  .transform((data) => ({
    redirectStatus: data.redirect_status,
    setupIntentSecret: data.setup_intent_client_secret,
    upgradePlanId: data.upgradePlanId,
  }));

export const Route = createFileRoute('/_auth/_org/settings/plans')({
  validateSearch: zodValidator(PlansSearch),
  component: Plans,
  notFoundComponent: () => <p>Workspace not found!</p>,
  beforeLoad: async ({ context: { client, queryClient }, search }) => {
    const organizationId = (client.credentials as TokenCredentials).organizationId;

    const pendingPaymentMethodKey = getPendingPaymentMethodKey(organizationId);

    const paymentMethod = JSON.parse(sessionStorage.getItem(pendingPaymentMethodKey) ?? 'null') as PaymentMethod;
    const upgradePlanId = JSON.parse(sessionStorage.getItem(PENDING_PLAN_ID_KEY) ?? 'null') as string | null;

    if (search.redirectStatus === 'succeeded' && search.setupIntentSecret && paymentMethod && upgradePlanId) {
      try {
        const keyPrefix = organizationId;

        const updatedPaymentMethod = await client.updatePaymentMethod(paymentMethod.paymentMethodId, {
          stripeSetupIntentSecret: search.setupIntentSecret,
        });
        queryClient.invalidateQueries({
          queryKey: [keyPrefix, 'paymentMethods', 'item', toPaymentMethodId(updatedPaymentMethod.paymentMethodId)],
        });

        await client.updateOrganization('me', { paymentMethodId: paymentMethod.paymentMethodId });
        queryClient.invalidateQueries({
          queryKey: [keyPrefix, 'organizations', 'item', toOrganizationId('me')],
        });

        sessionStorage.removeItem(pendingPaymentMethodKey);
      } catch (error) {
        console.error(error);
        throw redirect({ to: '/settings/plans' });
      }

      throw redirect({
        to: '/settings/plans',
        search: { upgradePlanId },
      });
    }
  },
});
